import moment from 'moment';
import writeXlsxFile, { type Cell, type SheetData } from 'write-excel-file';

export type TalentReviewExportEmployee = {
	id: string;
	firstName: string;
	lastName: string;
	jobTitle: string;
	departmentName: string;
	managerName: string;
	xTick: string;
	yTick: string;
	lastPlaced: string;
};

export type TalentReviewExportCell = {
	title: string;
	employees?: TalentReviewExportEmployee[];
};

const HEADER_STYLE = {
	fontWeight: 'bold',
	backgroundColor: '#eeeeee',
} as const;

export type TalentReviewExportLabels = {
	cell: string;
	employee: string;
	jobTitle: string;
	department: string;
	manager: string;
	xAxis: string;
	yAxis: string;
	lastPlaced: string;
};

const employeeName = (employee: TalentReviewExportEmployee) =>
	`${employee.firstName} ${employee.lastName}`.trim();

function placedOn(localDate: string): Date | undefined {
	const date = moment.utc(localDate, 'YYYY-MM-DD', true);

	return date.isValid() ? date.toDate() : undefined;
}

/**
 * One row per placed person, grouped by cell in the order the grid shows them.
 * @param rows grid rows, top to bottom, with the people currently in each cell
 */
export function buildTalentReviewExportRows(rows: TalentReviewExportCell[][]): Array<{
	cell: string;
	employee: TalentReviewExportEmployee;
}> {
	return rows.flatMap((row) =>
		row.flatMap((cell) => {
			const employees = [...(cell.employees ?? [])].sort((left, right) => {
				const byName = employeeName(left).localeCompare(employeeName(right), undefined, {
					sensitivity: 'base',
				});

				return byName || left.id.localeCompare(right.id);
			});

			return employees.map((employee) => ({ cell: cell.title, employee }));
		}),
	);
}

export function buildTalentReviewExportSheet(
	rows: TalentReviewExportCell[][],
	labels: TalentReviewExportLabels,
): SheetData {
	const header: Cell[] = [
		labels.cell,
		labels.employee,
		labels.jobTitle,
		labels.department,
		labels.manager,
		labels.xAxis,
		labels.yAxis,
		labels.lastPlaced,
	].map((value) => ({ ...HEADER_STYLE, value }));

	const body: SheetData = buildTalentReviewExportRows(rows).map(({ cell, employee }) => {
		const placed = placedOn(employee.lastPlaced);

		return [
			{ value: cell },
			{ value: employeeName(employee) },
			{ value: employee.jobTitle },
			{ value: employee.departmentName },
			{ value: employee.managerName },
			{ value: employee.xTick },
			{ value: employee.yTick },
			placed ? { type: Date, value: placed, format: 'yyyy-mm-dd' } : { value: '' },
		];
	});

	return [header, ...body];
}

function sheetName(gridName: string): string {
	return (
		gridName
			.replace(/[:\\/?*[\]]/g, ' ')
			.trim()
			.slice(0, 31) || 'Sheet1'
	);
}

export async function exportTalentReviewToExcel({
	rows,
	labels,
	gridName,
}: {
	rows: TalentReviewExportCell[][];
	labels: TalentReviewExportLabels;
	gridName: string;
}): Promise<void> {
	await writeXlsxFile(buildTalentReviewExportSheet(rows, labels), {
		fileName: `${gridName}.xlsx`,
		sheet: sheetName(gridName),
		columns: [
			{ width: 22 },
			{ width: 24 },
			{ width: 24 },
			{ width: 24 },
			{ width: 24 },
			{ width: 18 },
			{ width: 18 },
			{ width: 16 },
		],
	});
}
